import React from 'react';
import { Typography } from 'antd';
import { EnvironmentOutlined, ClockCircleOutlined, PhoneOutlined, ShopOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface Props {
  name: string | null;
  subtitle: string | null;
  address: string | null;
  workingHours: string | null;
  phone: string | null;
  mapUrl: string | null;
  onMapOpen: () => void;
  onPhoneCall: () => void;
}

interface InfoRowProps {
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  value: string;
  action?: { label: string; onClick: () => void };
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, iconColor, iconBg, value, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{
      width: 32, height: 32, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
      background: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, color: iconColor,
    }}>
      {icon}
    </div>
    <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text)', flex: 1 }}>
      {value}
    </Typography.Text>
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 3,
          background: 'rgba(51,144,236,0.10)',
          color: 'var(--bl-primary)',
          border: 'none', borderRadius: 'var(--bl-radius-sm)', padding: '10px 12px',
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'inherit', flexShrink: 0,
          WebkitTapHighlightColor: 'transparent',
          minHeight: 44,
        }}
      >
        {action.label}
        <RightOutlined style={{ fontSize: 9 }} />
      </button>
    )}
  </div>
);

export const BusinessCard: React.FC<Props> = ({
  name, subtitle, address, workingHours, phone, mapUrl, onMapOpen, onPhoneCall,
}) => {
  const { t } = useTranslation('customer');
  const hasAny = name !== null || subtitle !== null || address !== null || workingHours !== null || phone !== null;
  if (!hasAny) return null;

  return (
    <div style={{
      margin: '10px 16px 0',
      borderRadius: 'var(--bl-radius-lg)',
      background: 'var(--bl-surface)',
      border: '1px solid var(--bl-divider)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid var(--bl-divider)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
          background: 'rgba(217,119,6,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, color: 'rgba(217,119,6,1)',
        }}>
          <ShopOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text)', display: 'block' }}>
            {name ?? t('profile.business.fallbackName')}
          </Typography.Text>
          {subtitle !== null && (
            <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text-secondary)' }}>
              {subtitle}
            </Typography.Text>
          )}
        </div>
      </div>

      {/* Info rows */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {address !== null && (
          <InfoRow
            icon={<EnvironmentOutlined />}
            iconColor="rgba(217,119,6,1)"
            iconBg="rgba(217,119,6,0.10)"
            value={address}
            action={mapUrl !== null ? { label: t('profile.business.mapAction'), onClick: onMapOpen } : undefined}
          />
        )}
        {workingHours !== null && (
          <InfoRow
            icon={<ClockCircleOutlined />}
            iconColor="var(--bl-primary)"
            iconBg="rgba(51,144,236,0.10)"
            value={workingHours}
          />
        )}
        {phone !== null && (
          <InfoRow
            icon={<PhoneOutlined />}
            iconColor="rgba(31,139,76,1)"
            iconBg="rgba(31,139,76,0.10)"
            value={phone}
            action={{ label: t('profile.business.callAction'), onClick: onPhoneCall }}
          />
        )}
      </div>
    </div>
  );
};
